// Browser-friendly DID wallet. Uses Web Crypto (P-256) when available and
// localStorage for persistence. Falls back to in-memory no-op in restricted
// environments.

type DIDStore = { keys: Record<string,{ jwk:any }>; entries: Array<{ companyId:string; type:string; data:any; sig:string; ts:string }> };

const LS_KEY = 'evat:did';

function load(): DIDStore {
  try { const raw = typeof window !== 'undefined' ? window.localStorage.getItem(LS_KEY) : null; return raw ? (JSON.parse(raw) as any) : { keys:{}, entries:[] }; } catch { return { keys:{}, entries:[] }; }
}
function save(db: DIDStore) { try { if (typeof window !== 'undefined') window.localStorage.setItem(LS_KEY, JSON.stringify(db)); } catch {} }

export async function generateKey(companyId: string): Promise<{ keyId: string; pubJwk: any }>{
  if (typeof window === 'undefined' || !window.crypto?.subtle) { const keyId = `did-${companyId}`; const db = load(); db.keys[keyId] = { jwk: { kty:'EC', crv:'P-256' } }; save(db); return { keyId, pubJwk: db.keys[keyId].jwk }; }
  const key = await window.crypto.subtle.generateKey({ name:'ECDSA', namedCurve:'P-256' }, true, ['sign','verify']);
  const pubJwk = await window.crypto.subtle.exportKey('jwk', key.publicKey);
  const privJwk = await window.crypto.subtle.exportKey('jwk', key.privateKey);
  const db = load(); const keyId = `did-${companyId}`; db.keys[keyId] = { jwk: privJwk }; save(db);
  return { keyId, pubJwk };
}

export async function sign(companyId: string, payload: object): Promise<{ keyId: string; sig: string }>{
  const db = load(); const keyId = `did-${companyId}`;
  if (!db.keys[keyId]) await generateKey(companyId);
  if (typeof window === 'undefined' || !window.crypto?.subtle) { const sig = btoa(JSON.stringify(payload)).slice(0,32); db.entries.push({ companyId, type:'sign', data: payload, sig, ts: new Date().toISOString() }); save(db); return { keyId, sig }; }
  const key = await window.crypto.subtle.importKey('jwk', db.keys[keyId].jwk, { name:'ECDSA', namedCurve:'P-256' }, false, ['sign']);
  const data = new TextEncoder().encode(JSON.stringify(payload));
  const sigBuf = await window.crypto.subtle.sign({ name:'ECDSA', hash:'SHA-256' }, key, data);
  const sig = Buffer.from(new Uint8Array(sigBuf)).toString('hex');
  db.entries.push({ companyId, type:'sign', data: payload, sig, ts: new Date().toISOString() }); save(db);
  return { keyId, sig };
}

export async function verify(companyId: string, payload: object, sig: string): Promise<boolean> {
  const db = load(); const keyId = `did-${companyId}`; if (!db.keys[keyId]) return false;
  if (typeof window === 'undefined' || !window.crypto?.subtle) { return typeof sig === 'string' && sig.length > 0; }
  const pubJwk = await (async ()=> { const { jwk } = db.keys[keyId]; const pub = { ...jwk }; delete (pub as any).d; return pub; })();
  const key = await window.crypto.subtle.importKey('jwk', pubJwk, { name:'ECDSA', namedCurve:'P-256' }, false, ['verify']);
  const data = new TextEncoder().encode(JSON.stringify(payload));
  const sigBytes = Uint8Array.from(Buffer.from(sig, 'hex'));
  return window.crypto.subtle.verify({ name:'ECDSA', hash:'SHA-256' }, key, sigBytes, data);
}



